
import { useState } from 'react';

interface Episode {
  id: number;
  title: string;
  description: string;
  duration: string;
  date: string;
  image: string;
  category: string;
  isPlaying: boolean;
  youtubeUrl: string;
  spotifyUrl: string;
}

export default function LatestEpisodes() {
  const [playingEpisode, setPlayingEpisode] = useState<number | null>(null);

  const episodes: Episode[] = [
    {
      id: 1,
      title: "THE CHOP UP W | Kendrick Jones",
      description: "An inspiring conversation with Kendrick Jones about his journey, mindset, and the hustle behind his success. Dive deep into the strategies that drive peak performance.",
      duration: "31:43",
      date: "Dec 10, 2024",
      image: "https://i.ytimg.com/vi/Xv7YCVwdun0/hqdefault.jpg",
      category: "Interview",
      isPlaying: false,
      youtubeUrl: "https://youtu.be/Xv7YCVwdun0",
      spotifyUrl: "https://open.spotify.com/episode/4rOoJ6Egrf8K2IrywzwOMk"
    },
    {
      id: 2,
      title: "MHP Episode 44 : RHYTHM & HUSTLE ~ Masters of the Tables",
      description: "Dive into the world of rhythm and hustle with the masters of the tables. Learn about the dedication, skill, and passion that drives success in the music industry.",
      duration: "45:22",
      date: "Nov 28, 2024",
      image: "https://i.ytimg.com/vi/UDKpeZu8Bqg/hqdefault.jpg",
      category: "Interview",
      isPlaying: false,
      youtubeUrl: "https://youtu.be/UDKpeZu8Bqg",
      spotifyUrl: "https://open.spotify.com/episode/0VgO2pM4eugqC5c8UVynsP"
    },
    {
      id: 3,
      title: "The Chop Up: The Hustle Behind Beastmode Co | BE MORE CONSISTENT",
      description: "Explore the entrepreneurial journey behind Beastmode Co and discover the secrets to building consistency in business and life. A must-watch for aspiring entrepreneurs.",
      duration: "8:31",
      date: "Nov 21, 2024",
      image: "https://i.ytimg.com/vi/Z_mZ6EFQDfQ/hqdefault.jpg",
      category: "Business",
      isPlaying: false,
      youtubeUrl: "https://youtu.be/Z_mZ6EFQDfQ",
      spotifyUrl: "https://open.spotify.com/episode/1A4qwqKOaEAXq4jA4GPgw1"
    },
    {
      id: 4,
      title: "The Chop Up: The Hustle Behind Mr. Interior | Interior Design Edition",
      description: "Dive into the creative world of interior design with Mr. Interior. Learn how passion, creativity, and hustle combine to build a successful design business.",
      duration: "16:47",
      date: "Nov 14, 2024",
      image: "https://i.ytimg.com/vi/kqYGC5CQoyc/hqdefault.jpg",
      category: "Creative Business",
      isPlaying: false,
      youtubeUrl: "https://youtu.be/kqYGC5CQoyc",
      spotifyUrl: "https://open.spotify.com/episode/6Im9k8u9OHyABdk0EEHLZH"
    }
  ];

  const playOnSpotify = (spotifyUrl: string, episodeId: number) => {
    setPlayingEpisode(episodeId);
    window.open(spotifyUrl, '_blank');
    // Reset playing state after a short delay
    setTimeout(() => setPlayingEpisode(null), 2000);
  };

  const openYouTube = (youtubeUrl: string) => {
    window.open(youtubeUrl, '_blank');
  };

  return (
    <section className="py-16 md:py-20 bg-gray-900">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Latest Episodes
          </h2>
          <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto">
            Dive into our most recent conversations with industry leaders, entrepreneurs, and visionaries who embody the hustle.
          </p>
        </div>
        
        <div className="grid gap-6 md:gap-8 max-w-7xl mx-auto">
          {episodes.map((episode) => (
            <div key={episode.id} className="bg-gray-800 border border-gray-700 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <div className="p-4 md:p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row gap-4 md:gap-6">
                  <div className="flex-shrink-0 mx-auto sm:mx-0">
                    <img 
                      src={episode.image} 
                      alt={episode.title}
                      className="w-32 h-24 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-xl object-cover object-top"
                    />
                  </div>
                  
                  <div className="flex-1 text-center sm:text-left">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3 justify-center sm:justify-start">
                      <span className="px-3 py-1 bg-red-600 text-white text-sm font-semibold rounded-full inline-block">
                        {episode.category}
                      </span>
                      <span className="text-gray-300 text-base md:text-lg">{episode.date}</span>
                    </div>
                    
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4 line-clamp-2 leading-tight group-hover:text-red-400 transition-colors">
                      {episode.title}
                    </h3>
                    
                    <p className="text-gray-400 text-base md:text-lg mb-4 md:mb-6 line-clamp-3 leading-relaxed">
                      {episode.description}
                    </p>
                    
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <button 
                          onClick={() => playOnSpotify(episode.spotifyUrl, episode.id)}
                          className="w-12 h-12 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors cursor-pointer"
                          title="Play on Spotify"
                        >
                          <i className={`${playingEpisode === episode.id ? 'ri-pause-fill' : 'ri-play-fill'} text-lg`}></i>
                        </button>
                        
                        <div className="flex items-center gap-2 text-gray-400">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-time-line text-sm"></i>
                          </div>
                          <span className="text-gray-300 text-base md:text-lg">{episode.duration}</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <button 
                          onClick={() => playOnSpotify(episode.spotifyUrl, episode.id)}
                          className="w-10 h-10 flex items-center justify-center bg-green-600 hover:bg-green-700 rounded-full transition-colors cursor-pointer"
                          title="Listen on Spotify"
                        >
                          <i className="ri-spotify-fill text-white"></i>
                        </button>
                        <button 
                          onClick={() => openYouTube(episode.youtubeUrl)}
                          className="w-10 h-10 flex items-center justify-center bg-white hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
                          title="Watch on YouTube"
                        >
                          <i className="ri-youtube-fill text-red-600"></i>
                        </button>
                        <button className="w-10 h-10 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded-full transition-colors cursor-pointer">
                          <i className="ri-share-line text-gray-300"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-8 md:mt-12">
          <button 
            onClick={() => window.open('https://www.youtube.com/@themusclehustlepodcast', '_blank')}
            className="bg-white hover:bg-gray-200 text-black px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            Watch All Episodes on YouTube
          </button>
        </div>
      </div>
    </section>
  );
}
